import React, { useCallback, useEffect, useId, useRef } from 'react';
import NiceModal, { useModal } from '@ebay/nice-modal-react';
import styled from 'styled-components';

import useGrowthPlannerHandoff from 'containers/Intelligence/handoff/useGrowthPlannerHandoff';
import { TCustomerInsights } from 'containers/Intelligence/i18n/TModules';
import { intelligenceScopeDeclarations, tokens } from 'containers/Intelligence/theme';
import { intelligenceScrollbar } from 'containers/Intelligence/theme/scrollbar';
import { IntelligenceFilters } from 'containers/Intelligence/types';
import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';
import usePerx3Redesign from 'hooks/usePerx3Redesign';
import { formatNumber } from 'utils/formatters';

import HandoffCheckList from '../HandoffCheckList';
import HandoffMetricOption from '../HandoffMetricOption';
import HandoffResultState, { IHandoffResultAction } from '../HandoffResultState';
import { perx3PrimaryButton } from '../primaryButton';

export interface IGrowthPlannerSliderProps {
  cardKey: string;
  /** The originating card's display name; the first half of the panel's context line. */
  cardLabel: string;
  /** The product or industry the card was read under; omitted leaves the card name alone. */
  productLabel?: string;
  filters?: IntelligenceFilters;
  /** `useCardVisibility().canSendToGrowthPlanner` for the originating card. */
  permitted?: boolean;
  onClose?: () => void;
}

const PANEL_WIDTH = '520px';
const PANEL_HEIGHT = '900px';
const HEADER_HEIGHT = '81px';
const CLOSE_BUTTON_SIZE = '34px';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])'
].join(',');

const CONTENT_SCOPE_SELECTOR = '[data-testid="intelligence-content-scope"]';

// The panel is portalled to the modal root, which leaves the page's token scope behind, so the
// scope is re-declared here on the element every descendant inherits from - under the flag's OWN
// variant, not a pinned dark one (see `intelligenceScopeDeclarations`).
const Overlay = styled.div<{ $open: boolean; $perx3: boolean }>`
  ${({ $perx3 }) => intelligenceScopeDeclarations($perx3)}

  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  background: rgba(0, 0, 0, 0.5);
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  transition: opacity 220ms ease;
`;

const Panel = styled.aside<{ $open: boolean }>`
  display: flex;
  flex-direction: column;
  width: ${PANEL_WIDTH};
  max-width: 100%;
  /* Sized in px from the design frame. No viewport-height rule here: the route's content scope
     already owns that, and a second one would double-count mobile Safari's toolbars. */
  height: ${PANEL_HEIGHT};
  max-height: 100%;
  background: ${tokens.color['surface-section']};
  border-left: 1px solid ${tokens.color['border-subtle']};
  /* 589:33479 draws the shell as a floating 520x900 panel at corner radius/16; it shipped square.
     The panel is flush to the viewport's right edge where the file insets it, so the two right
     corners round against that edge -- the inset is a layout decision for the design owner and is
     deliberately not taken here. PLA-8815. */
  border-radius: ${tokens.radius['16']};
  box-shadow: ${tokens.elevation.overlay};
  color: ${tokens.color['text-primary']};
  transform: translateX(${({ $open }) => ($open ? '0' : '100%')});
  transition: transform 220ms ease;

  &:focus {
    outline: none;
  }
`;

const Header = styled.header`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex: 0 0 auto;
  min-height: ${HEADER_HEIGHT};
  padding: ${tokens.space['20']} ${tokens.space['24']};
  border-bottom: 1px solid ${tokens.color['border-subtle']};
`;

const Title = styled.h2`
  margin: 0;
  color: ${tokens.color['text-primary']};
  font-size: ${tokens.type['heading4-size']};
  font-weight: 700;
  line-height: 23px;
`;

const Context = styled.p`
  margin: ${tokens.space['4']} 0 0;
  color: ${tokens.color['text-secondary']};
  font-size: ${tokens.type['body-size']};
  font-weight: ${tokens.type['body-weight']};
  line-height: ${tokens.type['body-line']};
`;

const CloseButton = styled.button`
  flex: 0 0 auto;
  width: ${CLOSE_BUTTON_SIZE};
  height: ${CLOSE_BUTTON_SIZE};
  padding: 0;
  border: none;
  border-radius: ${tokens.radius.sm};
  background: ${tokens.color['surface-card-alt']};
  color: ${tokens.color['text-primary']};
  cursor: pointer;
  font-size: 16px;
  line-height: 19px;

  &:focus-visible {
    outline: none;
    box-shadow: ${tokens.elevation['focus-ring']};
  }
`;

const Body = styled.div`
  flex: 1 1 auto;
  overflow-y: auto;
  padding: ${tokens.space['24']};

  ${intelligenceScrollbar('y')}

  /* Safari < 14.1 has no flex gap (conventions/components/cross-browser.md § 5). */
  > * + * {
    margin-top: ${tokens.space['20']};
  }
`;

// `589:33487` draws this heading as `1 · Choose actionable metrics` — Inter 700 / 11 / lh 100% in
// --is-color-text-primary, and in the file's own sentence case. Its sibling `589:33521` reads the
// same, so neither the uppercase treatment nor the muted ink it used to carry is the file's.
const MetricListHeading = styled.h3`
  margin: 0 0 ${tokens.space['12']};
  color: ${tokens.color['text-primary']};
  font-size: ${tokens.type['handoff-step-size']};
  font-weight: ${tokens.type['handoff-step-weight']};
  line-height: ${tokens.type['handoff-step-line']};
`;

const MetricList = styled.div`
  > * + * {
    margin-top: ${tokens.space['8']};
  }
`;

/* The file PINS the summary in the footer with the action, where this scrolled it in the body inside
   a card: `1 metric selected` and `10,000 eligible customers` share ONE row, left and right, with no
   surface behind them, and the together-note sits under that behind a warning triangle. Read off the
   drawer's own frame (589:33479) rendered at 83%, so the arrangement is the file's; the exact
   footer metrics are not node-measured and stay on the space scale. PLA-8815. */
const Summary = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
`;

const SummaryLine = styled.p`
  margin: 0;
  color: ${tokens.color['text-primary']};
  font-size: ${tokens.type['body-size']};
  font-weight: ${tokens.type['body-weight']};
  line-height: ${tokens.type['body-line']};
`;

const TogetherNote = styled.p`
  display: flex;
  margin: 0;
  margin-top: ${tokens.space['8']};
  color: ${tokens.color['text-muted']};
  font-size: ${tokens.type['body-size']};
  font-weight: ${tokens.type['body-weight']};
  line-height: ${tokens.type['body-line']};
  text-align: left;
`;

// The file marks the note with a triangle in --is-warning, which is its #F5A623 exactly. Decorative:
// the sentence beside it already says what it says, so a second announcement would be noise.
const NoteMark = styled.span`
  flex: 0 0 auto;
  margin-right: ${tokens.space['8']};
  color: ${tokens.color.warning};
`;

const Footer = styled.footer`
  flex: 0 0 auto;
  padding: ${tokens.space['16']} ${tokens.space['24']} ${tokens.space['24']};
  border-top: 1px solid ${tokens.color['border-subtle']};
`;

/* Longhand, and on the button rather than a `> * + *` rhythm on the footer: that rule and the
   button's own reset are both single-class, and that tie is what zeroed this panel's other steps. */
const FooterAction = styled.div`
  margin-top: ${tokens.space['16']};
`;

const ContinueArrow = styled.span`
  margin-left: ${tokens.space['8']};
`;

// `589:33543`: 472 x 43 at a flat 10px radius, which is what the panel's 24px gutters already give
// it at full width. It carries no effects layer either, on the same Properties read that prints
// `Shadows and blurs` for the panel around it (`615:2672`), so there is no elevation at rest here
// and the focus ring has nothing to composite over. The fill it draws is #4F46E5 and is held back
// with the card CTA's for the same reason — see `GrowthPlannerCta`.
const ContinueButton = styled.button<{ $perx3: boolean }>`
  width: 100%;
  padding: ${tokens.space['12']} ${tokens.space['16']};
  border: none;
  border-radius: ${tokens.radius['10']};
  background: ${tokens.color['accent-purple']};
  color: ${tokens.color['text-primary']};
  cursor: pointer;
  font-size: ${tokens.type['body-size']};
  font-weight: 700;
  line-height: ${tokens.type['body-line']};

  &:disabled {
    background: ${tokens.color['surface-card-alt']};
    color: ${tokens.color['text-muted']};
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${tokens.elevation['focus-ring']};
  }

  /* Round 4 (user): the modal footer's CTA takes the redesign's black primary treatment. Layered
     after the base rules, and the disabled branch above still wins on the inert state. */
  ${({ $perx3 }) => $perx3 && perx3PrimaryButton}
`;

/**
 * The 520 x 900 Growth Planner slide-out. It is a hand-built panel rather than an antd `Drawer`
 * (chosen by the FR owner), so the focus trap, the scroll lock, ESC-to-close and the enter/exit
 * animation are implemented here — nothing else provides them. PLA-8815.
 */
export const GrowthPlannerSliderPanel: React.FC<IGrowthPlannerSliderProps> = ({
  cardKey,
  cardLabel,
  productLabel,
  filters,
  permitted = true,
  onClose
}) => {
  const { t } = useMultiLanguageTranslation();
  const modal = useModal();
  const isPerx3 = usePerx3Redesign();
  const panelRef = useRef<HTMLElement>(null);
  const titleId = useId();

  const {
    status,
    metrics,
    selectedKeys,
    toggleMetric,
    checks,
    canContinue,
    selectedCount,
    eligibleCustomers,
    errorRef,
    send
  } = useGrowthPlannerHandoff({ cardKey, filters, permitted });

  // Focus starts on the panel and returns to whatever opened it, so a keyboard reader is neither
  // dropped at the top of the page behind the overlay nor left inside a removed subtree.
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;

    panelRef.current?.focus();

    return () => {
      opener?.focus?.();
    };
  }, []);

  // The FR requires the page behind the panel to stay put. The lock goes on the route's content
  // scope, which is the element that owns the page height.
  useEffect(() => {
    const scope = document.querySelector<HTMLElement>(CONTENT_SCOPE_SELECTOR) ?? document.body;
    const previousOverflow = scope.style.overflow;

    scope.style.overflow = 'hidden';

    return () => {
      scope.style.overflow = previousOverflow;
    };
  }, []);

  const handleClose = useCallback(() => {
    if (onClose) {
      onClose();
    }

    // Resolve before hiding: `NiceModal.show` callers await the resolve, so hiding first leaves the
    // awaited promise hanging (conventions/modals.md § Common Mistakes).
    modal.resolve();
    modal.hide();
  }, [modal, onClose]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      handleClose();

      return;
    }

    if (event.key !== 'Tab' || !panelRef.current) {
      return;
    }

    const focusable = Array.from(
      panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
    );

    if (focusable.length === 0) {
      event.preventDefault();

      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;
    const outside = !panelRef.current.contains(active);

    if (event.shiftKey && (outside || active === first)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (outside || active === last)) {
      event.preventDefault();
      first.focus();
    }
  };

  // The panel has no antd `afterClose`, so the instance is removed when the exit animation ends.
  // Without this pairing `open={modal.visible}` leaks the instance (conventions/modals.md § 3).
  const handleTransitionEnd = (event: React.TransitionEvent<HTMLElement>) => {
    // The panel transitions one property and nothing inside it transitions at all, so the origin
    // check is what distinguishes the exit animation from a descendant's bubbled event.
    if (event.target !== event.currentTarget) {
      return;
    }

    if (!modal.visible) {
      modal.remove();
    }
  };

  const closeLabel = t(`${TCustomerInsights}.Handoff.Close`);
  const context = productLabel
    ? t(`${TCustomerInsights}.Handoff.PanelContext`, { card: cardLabel, product: productLabel })
    : cardLabel;

  // The receipt rows the design draws are deliberately absent: their labels have no i18n key and
  // this unit may only author `Handoff.Checks.*` (see the build handoff for the follow-up).
  const resultActionsFor = (): IHandoffResultAction[] => {
    if (status === 'failed') {
      return [
        { label: t(`${TCustomerInsights}.Actions.Retry`), onClick: send, tone: 'primary' },
        { label: closeLabel, onClick: handleClose, tone: 'secondary' }
      ];
    }

    if (status === 'success') {
      return [{ label: closeLabel, onClick: handleClose, tone: 'primary' }];
    }

    return [];
  };

  return (
    <Overlay $open={modal.visible} $perx3={isPerx3} data-testid="growth-planner-slider-overlay">
      <Panel
        ref={panelRef}
        $open={modal.visible}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onKeyDown={handleKeyDown}
        onTransitionEnd={handleTransitionEnd}
        data-testid="growth-planner-slider"
        data-status={status}
      >
        <Header>
          <div>
            <Title id={titleId} data-testid="growth-planner-slider-title">
              {t(`${TCustomerInsights}.Handoff.PanelTitle`)}
            </Title>
            <Context data-testid="growth-planner-slider-context">{context}</Context>
          </div>
          <CloseButton
            type="button"
            onClick={handleClose}
            aria-label={closeLabel}
            data-testid="growth-planner-slider-close"
          >
            ×
          </CloseButton>
        </Header>

        <Body>
          {status === 'idle' ? (
            <>
              <div>
                <MetricListHeading data-testid="growth-planner-slider-metrics-heading">
                  {t(`${TCustomerInsights}.Handoff.MetricListHeading`)}
                </MetricListHeading>
                <MetricList data-testid="growth-planner-slider-metrics">
                  {metrics.map(metric => (
                    <HandoffMetricOption
                      key={metric.key}
                      name={metric.name}
                      total={metric.total}
                      eligible={metric.eligible}
                      confidence={metric.confidence}
                      unavailableReason={metric.unavailableReason}
                      selected={selectedKeys.includes(metric.key)}
                      onToggle={() => toggleMetric(metric.key)}
                    />
                  ))}
                </MetricList>
              </div>

              <HandoffCheckList checks={checks} />
            </>
          ) : (
            <HandoffResultState
              status={status}
              actions={resultActionsFor()}
              footnote={errorRef ?? undefined}
            />
          )}
        </Body>

        {status === 'idle' || status === 'sending' ? (
          <Footer>
            {/* Only while the panel is still being filled in: the sending frame draws its own
                column and states the counts there, so repeating them under it is not the file. */}
            {status === 'idle' ? (
              <>
                <Summary data-testid="growth-planner-slider-summary">
                  <SummaryLine data-testid="growth-planner-slider-selected-count">
                    {t(`${TCustomerInsights}.Handoff.SelectedCount`, { count: selectedCount })}
                  </SummaryLine>
                  <SummaryLine data-testid="growth-planner-slider-total-eligible">
                    {t(`${TCustomerInsights}.Handoff.TotalEligible`, {
                      customers: formatNumber(eligibleCustomers)
                    })}
                  </SummaryLine>
                </Summary>
                <TogetherNote data-testid="growth-planner-slider-together-note">
                  <NoteMark aria-hidden="true" data-testid="growth-planner-slider-note-mark">
                    &#9888;
                  </NoteMark>
                  {t(`${TCustomerInsights}.Handoff.TogetherNote`)}
                </TogetherNote>
              </>
            ) : null}

            <FooterAction>
              <ContinueButton
                type="button"
                onClick={send}
                disabled={!canContinue}
                $perx3={isPerx3}
                data-testid="growth-planner-slider-continue"
              >
                {t(`${TCustomerInsights}.Handoff.Continue`)}
                <ContinueArrow aria-hidden="true">&#8594;</ContinueArrow>
              </ContinueButton>
            </FooterAction>
          </Footer>
        ) : null}
      </Panel>
    </Overlay>
  );
};

const GrowthPlannerSlider = NiceModal.create(GrowthPlannerSliderPanel);

export default GrowthPlannerSlider;
